import {
  AfterViewInit,
  Component,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  NgModel,
  ValidationErrors,
} from '@angular/forms';
import { AutomatorDynamicVariableType, Timezone } from '@graphql';
import { TimezoneListService } from '@element-services';
import { Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import moment from 'moment';
import { FormFieldErrorComponent } from '../form-field-error/form-field-error.component';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { DigitsOnlyDirective } from '@shared/shared-providers';
@Component({
  selector: 'app-universal-form-control',
  templateUrl: './universal-form-control.component.html',
  styleUrls: ['./universal-form-control.component.scss'],
  standalone: true,
  imports: [
    FormFieldErrorComponent,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    FormsModule,
    CommonModule,
    TranslateModule,
    DigitsOnlyDirective,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UniversalFormControlComponent),
      multi: true,
    },
  ],
})
export class UniversalFormControlComponent
  implements OnChanges, OnInit, AfterViewInit, OnDestroy, ControlValueAccessor
{
  private timezoneListService = inject(TimezoneListService);

  @Input()
  fieldKind!: AutomatorDynamicVariableType;
  @Input()
  enumValues: { label: string; name: string }[] = [];
  @Input()
  placeholder = 'Value';
  @Input()
  isCustomField!: boolean;
  @Input()
  required = false;
  @Input()
  formSubmitted = false;
  @Input()
  controlErrors!: ValidationErrors;
  @Input()
  pastDateAllowed = true;
  @Input()
  formControl!: FormControl;
  @ViewChild('inputField', { static: false }) inputField!: NgModel;

  timezones$!: Observable<Timezone[]>;

  todayDateTime = moment().format('YYYY-MM-DDTHH:mm');
  inputValue: any;
  inputType!:
    'string' | 'number' | 'boolean' | 'date' | 'enum' | 'multiple_select' | 'datetime' | 'timezone';

  private inputSubject = new Subject<any>();
  private inputSubscription?: Subscription;
  private onChange = (value: any) => {};
  private onTouched = () => {};
  disabled = false;

  constructor() {
    // Setup debounced input handling for literal data - 300ms debounce
    this.inputSubscription = this.inputSubject
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((value: any) => {
        // Update the FormControl directly after debouncing
        if (this.formControl && !this.formControl.disabled) {
          this.formControl.setValue(value);
        }
      });
  }

  ngOnInit(): void {
    //console.log('[UniversalFormControl] ngOnInit called');
    //console.log('[UniversalFormControl] fieldKind:', this.fieldKind);
    //console.log('[UniversalFormControl] formControl:', this.formControl);
    this.setInputType();

    // Subscribe to existing form control value changes
    if (this.formControl) {
      //console.log('[UniversalFormControl] Setting up FormControl subscription');
      this.inputValue = this.formControl?.value ?? '';

      // Listen to external FormControl changes (like when variable is selected and FormControl is cleared)
      this.formControl.valueChanges.subscribe((value) => {
        //console.log('[UniversalFormControl] FormControl external change detected:', value);
        //console.log('[UniversalFormControl] FormControl disabled:', this.formControl.disabled);

        // Update input value when FormControl is changed externally
        if (value !== this.inputValue) {
          //console.log('[UniversalFormControl] Updating inputValue from external FormControl change');
          this.inputValue = value ?? '';
        }
      });
    }
  }

  ngAfterViewInit(): void {
    //console.log('[UniversalFormControl] ngAfterViewInit called');
    //console.log('[UniversalFormControl] inputField:', this.inputField);
    if (this.formSubmitted) {
      this.inputField.control.markAsTouched();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // tslint:disable:no-string-literal
    if (changes['fieldKind']) {
      this.setInputType();
    }
    if (changes['formSubmitted'] && changes['formSubmitted'].currentValue && this.inputField) {
      this.inputField.control.markAsTouched();
    }
    if (changes['controlErrors'] && changes['controlErrors'].currentValue && this.inputField) {
      this.inputField.control.setErrors(changes['controlErrors'].currentValue);
    }
    // tslint:enable:no-string-literal
  }

  ngOnDestroy(): void {
    if (this.inputSubscription) {
      this.inputSubscription.unsubscribe();
    }
  }

  emitValue(value: any) {
    this.inputValue = value;

    // Use debounced input for literal data
    this.inputSubject.next(value);

    // Also emit change immediately for ControlValueAccessor
    this.onChange(value);
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    this.inputValue = value ?? '';
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onBlur(): void {
    this.onTouched();
  }

  private setInputType(): void {
    switch (this.fieldKind) {
      case 'BOOLEAN':
        this.inputType = 'boolean';
        break;
      case 'LOCAL_DATE':
        this.inputType = 'date';
        break;
      case 'LOCAL_DATETIME':
        this.inputType = 'datetime';
        break;
      case 'ENUM':
      case 'LIST':
        this.inputType = 'enum';
        break;
      case 'DOUBLE':
      case 'INT':
      case 'LONG':
        this.inputType = 'number';
        break;
      case 'OBJECT':
        this.inputType = 'multiple_select';
        break;
      case 'ZONE_ID':
        this.timezones$ = this.timezoneListService.getTimezones();
        this.inputType = 'timezone';
        break;
      case 'FLOAT':
      case 'NONE':
      case 'STRING':
      case 'LOCAL_TIME':
      case 'TIMESTAMP':
        this.inputType = 'string';
        break;
      default: {
        console.warn('Unknown field kind', this.fieldKind);
        break;
      }
    }
  }
}
